import loadable from "@loadable/component";
import React from "react";
import { useDispatch } from "react-redux";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import { CampaignDetails } from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import withReduxProvider from "web/react/redux-provider";
import { CaptureType, openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import globals from "web/script/modules/globals";
import { clearCache } from "web/script/modules/rothko";
import { setCampaignHasBeenDismissed } from "web/script/utils/block-modal-campaigns";
import browser from "web/script/utils/browser";
import history from "web/script/utils/history";
import storage from "web/script/utils/storage";
import url from "web/script/utils/url";

const SendToAppChecklistModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-checklist-modal" */ "web/react/components/send-to-app-checklist-modal/send-to-app-checklist-modal"
        )
);

const SendToAppModal = loadable(
    () =>
        import(
            /* webpackChunkName: "send-to-app-modal" */ "web/react/components/send-to-app-modal/send-to-app-modal"
        )
);

const SaveForLaterSendToAppModal = loadable(
    () =>
        import(
            /* webpackChunkName: "save-for-later-send-to-app-modal" */ "web/react/components/save-for-later-send-to-app-modal/save-for-later-send-to-app-modal"
        )
);

// must add all usable titles to this array so they are included in the bundled translations
const TITLE_KEYS = [
    gettextNoop("feed.show_more.app_overlay_block.title_4"),
    gettextNoop("product_card.app_overlay_block.title_1"),
    gettextNoop("in_stock_product.related_show_more.app_overlay_block.title_1"),
    gettextNoop("oos_product.related_show_more.app_overlay_block.title_2"),
    gettextNoop("is_product.shop_now.app_overlay_block.title_1"),
    gettextNoop("product_card.oos_product_app_overlay_block.title_1"),
    gettextNoop("product_card.app_overlay_block.title_price_1"),
    gettextNoop("product_card.app_overlay_block.title_price_2"),
];

interface WebToAppCampaignModalProps {
    id: number | string;
    isOpen: boolean;
    onClose: () => void;
    onCtaClick: () => void;
    analyticsEventLabel: string;
    contextType: string;
    campaignDetails?: CampaignDetails;
    continueOptionHref?: string;
    onContinueClick?: (event) => void;
    productId?: number | string;
    productImageUrl?: string;
    appDeepLinkPath?: string;
    continueMessage?: string;
    productUrl?: string;
    captureType?: CaptureType;
}

export function WebToAppCampaignModal({
    id,
    isOpen,
    onClose,
    campaignDetails,
    continueOptionHref,
    onContinueClick,
    onCtaClick,
    analyticsEventLabel,
    contextType,
    productId,
    productImageUrl,
    appDeepLinkPath,
    continueMessage,
    productUrl,
    captureType,
}: WebToAppCampaignModalProps): JSX.Element | null {
    // WebToAppCampaigns with the same `analyticsEventLabel` require an `id` to avoid mismatched overlays
    const modalName = `modal-${analyticsEventLabel}-${id}`;
    const {
        branchLink,
        componentType,
        blockType,
        captureType: captureTypeKevel,
        cheapestPriceComparison,
    } = campaignDetails || {};
    const titleKey = campaignDetails?.titleKey || "";
    const appDeepLinkURL = appDeepLinkPath ? url.parse(appDeepLinkPath) : undefined;
    const dispatch = useDispatch();

    if (titleKey && !TITLE_KEYS.includes(titleKey)) {
        console.error("Invalid titleKey");
    }

    let title;
    if (cheapestPriceComparison) {
        if (cheapestPriceComparison.isCheapestPrice) {
            // "You have found the best price available for this item!"
            title = gettext("product_card.app_overlay_block.title_price_1");
        } else if (cheapestPriceComparison.percentagePriceDifference < 5) {
            // "We found a better price for this item in our App"
            title = gettext("product_card.app_overlay_block.title_price_2");
        } else {
            const discountPercentWithPercentSymbol = gettext(
                "general.integer_with_percent_symbol",
                { integer: cheapestPriceComparison.percentagePriceDifference }
            );
            // "Get a member-only benefit! Get {discount_percent_with_percent_symbol} off in the app"
            title = gettext("product_card.app_overlay_block.title_price_percentage", {
                discount_percent_with_percent_symbol: discountPercentWithPercentSymbol,
            });
        }
    } else {
        title = gettext(titleKey);
    }

    function onAfterOpen(): void {
        // check overlay state to avoid delay caused by hooks
        if (history.getCurrentState().overlay !== modalName) {
            history.pushOverlay(modalName);
        }
    }

    function onModalDismissed(): void {
        setCampaignHasBeenDismissed(contextType);
        onClose();
        const browserDesktop = browser.getViewport() === browser.VIEWPORTS.DESKTOP;

        if (blockType === "soft_block" && browserDesktop && continueOptionHref) {
            globals.window.location.href = continueOptionHref;
        }
    }

    function onModalClose(): void {
        history.popOverlay(modalName);
        onModalDismissed();
    }

    let continueOption;
    if (blockType === "soft_block" && (onContinueClick || continueOptionHref)) {
        continueOption = (
            <ContinueToOption
                href={continueOptionHref}
                continueMessage={continueMessage || gettext("general.continue_shopping_btn_text")}
                analyticsEventLabel={analyticsEventLabel}
                continueClick={(event) => {
                    onContinueClick?.(event);
                    storage.set("continueClicked", true, true);
                    onClose();

                    clearCache();
                }}
            />
        );
    }

    const extraContent = continueOption ? () => continueOption : undefined;

    const captureTypeToUse = captureType ? captureType : (captureTypeKevel as CaptureType);

    const customerCaptureOverlayTypes = [
        "app_or_email_signup",
        "customer_capture",
        "desktop_signup",
    ];
    if (isOpen && componentType && customerCaptureOverlayTypes.includes(componentType)) {
        dispatch(
            openCustomerCaptureOverlay({
                productId,
                productImageUrl,
                analyticsEventLabel,
                componentType,
                captureType: captureTypeToUse || "feeds_product_card",
                next: continueOptionHref || productUrl,
                appDeeplinkPath: appDeepLinkPath,
                onClose: onModalDismissed,
                onCtaClick: onCtaClick,
            })
        );
        return null;
    }

    return (
        <>
            {componentType === "checklist_modal" && (
                <SendToAppChecklistModal
                    url={branchLink}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                />
            )}
            {componentType === "generic_modal" && (
                <SendToAppModal
                    title={title}
                    url={branchLink}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    analyticsEventLabel={analyticsEventLabel}
                    onCtaClick={onCtaClick}
                    productImageURL={productImageUrl}
                    extraContent={extraContent}
                    onAfterOpen={onAfterOpen}
                    appDeepLinkURL={appDeepLinkURL}
                    showShippingDisclaimer={cheapestPriceComparison}
                />
            )}
            {componentType === "value_prop" && (
                <SaveForLaterSendToAppModal
                    appDeeplinkPath={appDeepLinkPath}
                    isOpen={isOpen}
                    onClose={onModalClose}
                    onCtaClick={onCtaClick}
                />
            )}
        </>
    );
}

export default withReduxProvider(WebToAppCampaignModal);
